import React, { useCallback, useMemo } from 'react';
import {
  Autocomplete,
  TextField,
  CircularProgress,
  Box,
  Typography,
  Skeleton,
  alpha,
  Chip,
  Tooltip
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  KeyboardArrowDown as ArrowDownIcon,
  SearchOutlined as SearchIcon
} from '@mui/icons-material';
import PropTypes from 'prop-types';
import { useLookup } from './useLookup';

// --- Virtualized Listbox for large datasets ---
const ITEM_HEIGHT = 44;
const MAX_VISIBLE_ITEMS = 8;
const LISTBOX_PADDING = 8;

const VirtualizedListbox = React.forwardRef(
  function VirtualizedListbox(props, ref) {
    const { children, ...other } = props;
    const items = React.Children.toArray(children);
    const itemCount = items.length;
    const listHeight =
      Math.min(itemCount, MAX_VISIBLE_ITEMS) * ITEM_HEIGHT +
      2 * LISTBOX_PADDING;

    const [scrollTop, setScrollTop] = React.useState(0);

    const handleScroll = useCallback((e) => {
      setScrollTop(e.target.scrollTop);
    }, []);

    const startIndex = Math.max(
      0,
      Math.floor((scrollTop - LISTBOX_PADDING) / ITEM_HEIGHT) - 2
    );
    const endIndex = Math.min(
      itemCount,
      Math.ceil((scrollTop + listHeight) / ITEM_HEIGHT) + 2
    );
    const totalHeight = itemCount * ITEM_HEIGHT + 2 * LISTBOX_PADDING;

    return (
      <ul
        ref={ref}
        {...other}
        onScroll={handleScroll}
        style={{
          maxHeight: listHeight,
          overflow: 'auto',
          position: 'relative',
          margin: 0,
          padding: 0,
          listStyle: 'none'
        }}
      >
        <div style={{ height: totalHeight, position: 'relative' }}>
          {items.slice(startIndex, endIndex).map((item, index) =>
            React.cloneElement(item, {
              style: {
                ...item.props.style,
                position: 'absolute',
                top: LISTBOX_PADDING + (startIndex + index) * ITEM_HEIGHT,
                left: 0,
                right: 0,
                height: ITEM_HEIGHT
              }
            })
          )}
        </div>
      </ul>
    );
  }
);

VirtualizedListbox.propTypes = {
  children: PropTypes.node
};

// Filter with limit to prevent processing too many items at once
// Türkçe karakter normalizasyonu (ş→s, ç→c, ğ→g, ü→u, ö→o, ı→i vb.)
const turkishCharMap = {
  ş: 's',
  Ş: 'S',
  ç: 'c',
  Ç: 'C',
  ğ: 'g',
  Ğ: 'G',
  ü: 'u',
  Ü: 'U',
  ö: 'o',
  Ö: 'O',
  ı: 'i',
  İ: 'I',
  '–': '-',
  '—': '-',
  '\u2018': "'",
  '\u2019': "'"
};
const turkishCharRegex = /[şŞçÇğĞüÜöÖıİ–—\u2018\u2019]/g;
const normalizeTurkish = (str) => {
  if (!str) return '';
  return str
    .replace(turkishCharRegex, (ch) => turkishCharMap[ch] || ch)
    .toLowerCase();
};

// Kısaltma haritası (normalize edilmiş key → normalize edilmiş açılımlar)
const abbreviationMap = {
  cm: ['camasir', 'makinesi'],
  kc: ['kurutma', 'makinesi', 'kurutucu'],
  lg: ['klima'],
  pc: ['bilgisayar'],
  tv: ['televizyon'],
  okc: ['okc'],
  'bul.mak': ['bulasik', 'makinesi'],
  'bul.mak.': ['bulasik', 'makinesi']
};

// Kısaltmaları açarak ek arama kelimeleri üretir
const expandAbbreviations = (words) => {
  const expanded = [];
  for (const w of words) {
    const found = abbreviationMap[w];
    if (found) {
      expanded.push(...found);
    } else {
      expanded.push(w);
    }
  }
  return expanded;
};

const defaultFilterOptions = (options, { inputValue }) => {
  const trimmed = inputValue.trim();
  if (!trimmed) return options.slice(0, 50);
  const words = normalizeTurkish(trimmed).split(/\s+/).filter(Boolean);
  const expanded = expandAbbreviations(words);
  return options
    .filter((option) => {
      const normalized = normalizeTurkish(option.name);
      return expanded.every((w) => normalized.includes(w));
    })
    .slice(0, 50);
};

// --- Static style objects (moved outside component to avoid re-creation on every render) ---
const noOptionsContent = (
  <Box sx={{ py: 2, textAlign: 'center' }}>
    <SearchIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
    <Typography color="text.secondary">Sonuç bulunamadı</Typography>
  </Box>
);

const paperProps = {
  elevation: 8,
  sx: {
    borderRadius: 1,
    mt: 1,
    overflow: 'hidden',
    border: '1px solid',
    borderColor: 'divider',
    '& .MuiAutocomplete-listbox': {
      p: 1,
      maxHeight: 300,
      '&::-webkit-scrollbar': { width: 6 },
      '&::-webkit-scrollbar-track': { background: 'transparent' },
      '&::-webkit-scrollbar-thumb': {
        background: alpha('#000', 0.2),
        borderRadius: 3
      }
    }
  }
};

const popperProps = {
  sx: {
    '& .MuiPaper-root': { boxShadow: '0 10px 40px rgba(0,0,0,0.12)' }
  },
  modifiers: [
    {
      name: 'sameWidth',
      enabled: true,
      phase: 'beforeWrite',
      requires: ['computeStyles'],
      fn: ({ state }) => {
        state.styles.popper.width = `${state.rects.reference.width}px`;
      },
      effect: ({ state }) => {
        state.elements.popper.style.width = `${state.elements.reference.offsetWidth}px`;
      }
    }
  ]
};

const staticAutocompleteStyles = {
  width: '100%',
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: alpha('#000', 0.15),
      borderWidth: 1
    },
    '&:hover fieldset': {
      borderColor: 'primary.light',
      borderWidth: 1
    },
    '&.Mui-focused fieldset': {
      borderColor: 'primary.main',
      borderWidth: 2,
      boxShadow: `0 0 0 3px ${alpha('#1976d2', 0.1)}`
    },
    '&.Mui-error fieldset': {
      borderColor: 'error.main',
      boxShadow: `0 0 0 3px ${alpha('#d32f2f', 0.1)}`
    }
  },
  '& .MuiAutocomplete-popupIndicator': {
    color: 'text.secondary',
    '&:hover': { backgroundColor: alpha('#000', 0.04) }
  },
  '& .MuiAutocomplete-popupIndicatorOpen': {
    transform: 'rotate(180deg)'
  },
  '& .MuiAutocomplete-clearIndicator': {
    color: 'text.secondary',
    '&:hover': { color: 'error.main', backgroundColor: alpha('#d32f2f', 0.08) }
  }
};

const compactInputStyles = {
  '& .MuiInputBase-root': { height: 32, fontSize: '0.8rem' },
  '& .MuiInputLabel-root': {
    fontSize: '0.75rem',
    transform: 'translate(14px, 8px) scale(1)',
    '&.MuiInputLabel-shrink': { transform: 'translate(14px, -6px) scale(0.75)' }
  },
  '& .MuiInputBase-input': { padding: '6px 10px', fontSize: '0.8rem' },
  '& .MuiInputAdornment-root': { marginRight: 0 }
};

const readOnlyInputSx = {
  backgroundColor: alpha('#000', 0.04),
  cursor: 'not-allowed',
  '& .MuiInputBase-input': { cursor: 'not-allowed', color: '#000000' },
  '& .MuiAutocomplete-endAdornment': { display: 'none' }
};

const inputLabelProps = {
  sx: {
    fontWeight: 500,
    '&.Mui-focused': { fontWeight: 600 }
  }
};

const inputFieldStyles = {
  width: '100%',
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: alpha('#000', 0.15),
      borderWidth: 1
    },
    '&:hover fieldset': { borderColor: 'primary.light', borderWidth: 1 },
    '&.Mui-focused fieldset': { borderColor: 'primary.main', borderWidth: 2 }
  }
};

const Combo = React.memo(
  ({
    // Tablo sorgusu için
    tableName,
    filter = '',
    // Function sorgusu için
    functionName,
    functionParameters,
    // Ortak parametreler
    label,
    idField = 'Id',
    textField = 'Name',
    connectionName = 'SarisDb',
    value,
    onChange,
    required = false,
    placeholder = 'Seçim yapınız...',
    size = 'medium',
    variant = 'outlined',
    disabled = false,
    readOnly = false,
    showSelectedChip = false,
    compact = false,
    orderBy = 'name', // 'name' (varsayılan) veya 'id' — sıralama alanı
    searchSplitChar, // Alan ayırıcı karakter (ör. '§') — çoklu alan araması için
    optionFilter, // (item) => bool — istemci tarafı seçenek filtresi
    staleTime = 500 * 60 * 1000, // Varsayılan: 5 dakika
    cacheTime = 700 * 60 * 1000, // Varsayılan: 10 dakika
    error = false, // Validasyon hatası durumu
    helperText = '', // Yardımcı metin
    sx = {}
  }) => {
    const { data, isLoading, isError } = useLookup({
      tableName,
      functionName,
      functionParameters,
      idField,
      textField,
      filter,
      connectionName,
      staleTime,
      cacheTime,
      orderBy
    });

    // Combo id'si için unique key oluştur
    // const comboId = functionName || tableName;

    // API'den gelen veriyi normalize et (Id -> id, Text -> name)
    // const normalizedData = React.useMemo(() => {
    //   if (!data) return [];
    //   return data.map((item, index) => {
    //     // API'den gelen veri formatına göre id ve name'i çıkar
    //     // Önce idField'ı kontrol et, sonra yaygın alan isimlerini dene
    //     const id = item[idField] ?? item.Value ?? item.value ?? item.Id ?? item.id ?? index;
    //     const name = item[textField] ?? item.Text ?? item.text ?? item.Name ?? item.name ?? '';
    //     return { id, name };
    //   });
    // }, [data, idField, textField]);

    const normalizedData = React.useMemo(() => {
      if (!data) return [];

      return data
        .map((item, index) => {
          const id =
            item[idField] ??
            item.Value ??
            item.value ??
            item.Id ??
            item.id ??
            index;
          const rawName =
            item[textField] ??
            item.Text ??
            item.text ??
            item.Name ??
            item.name ??
            '';
          const result = { id };
          if (searchSplitChar && rawName.includes(searchSplitChar)) {
            result._searchParts = rawName
              .split(searchSplitChar)
              .map((s) => s.trim());
            result.name = result._searchParts.join(' ');
          } else {
            result.name = rawName;
          }
          return result;
        })
        .filter((item) =>
          typeof optionFilter === 'function' ? optionFilter(item) : true
        )
        .sort((a, b) => {
          if (typeof orderBy === 'function') return orderBy(a, b);
          if (orderBy === 'id') {
            const aId = typeof a.id === 'number' ? a.id : Number(a.id);
            const bId = typeof b.id === 'number' ? b.id : Number(b.id);
            if (!isNaN(aId) && !isNaN(bId)) return aId - bId;
            return String(a.id).localeCompare(String(b.id), 'tr', {
              sensitivity: 'base'
            });
          }
          return a.name?.localeCompare(b.name, 'tr', { sensitivity: 'base' });
        });
    }, [data, idField, textField, orderBy, searchSplitChar, optionFilter]);

    // Value'yu normalizedData'dan bul - sadece ID varsa tam kaydı getir
    const actualValue = React.useMemo(() => {
      if (!value) return null;
      // Eğer value'da name yoksa veya boşsa, normalizedData'dan bul
      if (!value.name || value.name === '') {
        const found = normalizedData.find((item) => item.id == value.id);
        return found || value;
      }
      return value;
    }, [value, normalizedData]);

    // Çoklu alan araması için filtre fonksiyonu
    const filterFn = useMemo(() => {
      if (!searchSplitChar) return defaultFilterOptions;
      return (options, { inputValue }) => {
        const trimmed = inputValue.trim();
        if (!trimmed) return options.slice(0, 50);
        const words = normalizeTurkish(trimmed).split(/\s+/).filter(Boolean);
        const expanded = expandAbbreviations(words);
        return options
          .filter((option) => {
            if (option._searchParts) {
              const normalizedParts = option._searchParts.map((p) =>
                normalizeTurkish(p)
              );
              return expanded.every((w) =>
                normalizedParts.some((p) => p.includes(w))
              );
            }
            const normalized = normalizeTurkish(option.name);
            return expanded.every((w) => normalized.includes(w));
          })
          .slice(0, 50);
      };
    }, [searchSplitChar]);

    // Stable callback for onChange
    const handleChange = useCallback(
      (event, newValue) => onChange(newValue),
      [onChange]
    );

    // Memoized renderOption — avoids re-creating on every render (critical with 27 Combos)
    const renderOption = useCallback((props, option, state) => {
      const { key, ...otherProps } = props;
      const isSelected = state.selected;
      return (
        <Box
          key={key}
          component="li"
          {...otherProps}
          sx={{
            borderRadius: 2,
            mx: 0.5,
            mb: 0.5,
            py: 1.5,
            px: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            backgroundColor: isSelected
              ? alpha('#1976d2', 0.08)
              : 'transparent',
            '&:hover': {
              backgroundColor: isSelected
                ? alpha('#1976d2', 0.12)
                : alpha('#000', 0.04)
            },
            '&.Mui-focused': {
              backgroundColor: alpha('#1976d2', 0.08)
            }
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              flex: 1,
              minWidth: 0
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: isSelected
                  ? 'primary.main'
                  : alpha('#000', 0.2),
                flexShrink: 0
              }}
            />
            {option._searchParts ? (
              (() => {
                const searchWords = normalizeTurkish(state.inputValue || '')
                  .split(/\s+/)
                  .filter(Boolean);
                const expandedWords = expandAbbreviations(searchWords);
                const highlightText = (text) => {
                  if (!expandedWords.length) return text;
                  const norm = normalizeTurkish(text);
                  const ranges = [];
                  for (const w of expandedWords) {
                    let idx = norm.indexOf(w);
                    while (idx !== -1) {
                      ranges.push([idx, idx + w.length]);
                      idx = norm.indexOf(w, idx + 1);
                    }
                  }
                  if (!ranges.length) return text;
                  ranges.sort((a, b) => a[0] - b[0]);
                  const merged = [ranges[0]];
                  for (let i = 1; i < ranges.length; i++) {
                    const last = merged[merged.length - 1];
                    if (ranges[i][0] <= last[1])
                      last[1] = Math.max(last[1], ranges[i][1]);
                    else merged.push(ranges[i]);
                  }
                  const parts = [];
                  let prev = 0;
                  for (const [s, e] of merged) {
                    if (prev < s)
                      parts.push(<span key={prev}>{text.slice(prev, s)}</span>);
                    parts.push(
                      <span
                        key={`h${s}`}
                        style={{
                          backgroundColor: alpha('#1976d2', 0.15),
                          color: '#1565c0',
                          fontWeight: 600,
                          borderRadius: 2,
                          padding: '0 2px'
                        }}
                      >
                        {text.slice(s, e)}
                      </span>
                    );
                    prev = e;
                  }
                  if (prev < text.length)
                    parts.push(<span key={prev}>{text.slice(prev)}</span>);
                  return parts;
                };
                const hasMatch = (text) => {
                  if (!expandedWords.length) return false;
                  const n = normalizeTurkish(text);
                  return expandedWords.some((w) => n.includes(w));
                };
                const code = option._searchParts[0] || '';
                const desc = option._searchParts.slice(1).join(' ') || '';
                const codeMatched = hasMatch(code);
                const descMatched = hasMatch(desc);
                return (
                  <Tooltip title={option.name} placement="top" arrow>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flex: 1,
                        minWidth: 0,
                        overflow: 'hidden'
                      }}
                    >
                      <Typography
                        component="span"
                        sx={{
                          fontFamily:
                            '"JetBrains Mono", "Fira Code", "Consolas", monospace',
                          fontSize: '0.72rem',
                          fontWeight: 700,
                          color: codeMatched
                            ? '#1565c0'
                            : isSelected
                              ? 'primary.main'
                              : 'text.primary',
                          bgcolor: codeMatched
                            ? alpha('#1976d2', 0.08)
                            : alpha('#000', 0.04),
                          border: '1px solid',
                          borderColor: codeMatched
                            ? alpha('#1976d2', 0.3)
                            : alpha('#000', 0.08),
                          borderRadius: 1,
                          px: 0.75,
                          py: 0.25,
                          flexShrink: 0,
                          letterSpacing: '0.02em',
                          lineHeight: 1.4,
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {codeMatched ? highlightText(code) : code}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: isSelected || descMatched ? 600 : 400,
                          color: descMatched
                            ? '#1565c0'
                            : isSelected
                              ? 'primary.main'
                              : 'text.secondary',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          flex: 1,
                          fontSize: '0.8rem'
                        }}
                      >
                        {descMatched ? highlightText(desc) : desc}
                      </Typography>
                    </Box>
                  </Tooltip>
                );
              })()
            ) : (
              <Tooltip title={option.name} placement="top" arrow>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: isSelected ? 600 : 400,
                    color: isSelected ? 'primary.main' : 'text.primary',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flex: 1
                  }}
                >
                  {option.name}
                </Typography>
              </Tooltip>
            )}
          </Box>
          {isSelected && (
            <CheckIcon
              sx={{ fontSize: 18, color: 'primary.main', flexShrink: 0, ml: 1 }}
            />
          )}
        </Box>
      );
    }, []);

    // Memoized renderInput — avoids re-creating TextField on every render
    const renderInput = useCallback(
      (params) => (
        <TextField
          autoComplete="off"
          {...params}
          label={label}
          required={required}
          placeholder={placeholder}
          error={isError || error}
          variant={variant}
          helperText={
            isError ? 'Veriler yüklenirken bir hata oluştu' : helperText
          }
          slotProps={{
            input: {
              ...params.InputProps,
              readOnly: readOnly,
              endAdornment: (
                <React.Fragment>
                  {isLoading ? (
                    <CircularProgress
                      color="primary"
                      size={20}
                      thickness={5}
                      sx={{ mr: 1 }}
                    />
                  ) : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
              ...(readOnly && { sx: readOnlyInputSx })
            },
            inputLabel: {
              ...inputLabelProps,
              shrink: !!actualValue || undefined
            }
          }}
          sx={{
            ...inputFieldStyles,
            ...(compact && compactInputStyles)
          }}
        />
      ),
      [
        label,
        required,
        placeholder,
        isError,
        error,
        helperText,
        variant,
        readOnly,
        isLoading,
        compact,
        normalizedData?.length,
        actualValue
      ]
    );

    // Memoize the merged sx prop to avoid recalculation
    const autocompleteSx = useMemo(
      () => ({
        ...staticAutocompleteStyles,
        ...sx,
        ...(compact && compactInputStyles)
      }),
      [sx, compact]
    );

    // Memoize componentsProps
    const memoizedComponentsProps = useMemo(
      () => ({
        paper: paperProps,
        popper: popperProps
      }),
      []
    );

    // Loading skeleton with shimmer effect
    if (isLoading) {
      return (
        <Box sx={{ position: 'relative', width: '100%' }}>
          <Skeleton
            variant="rounded"
            width="100%"
            height={compact || size === 'small' ? 40 : 56}
            sx={{ borderRadius: 1 }}
          />
        </Box>
      );
    }

    return (
      <Tooltip title={label} placement="top" arrow>
        <span style={{ display: 'block' }}>
          <Autocomplete
            value={actualValue}
            onChange={handleChange}
            options={normalizedData}
            getOptionLabel={(option) => option?.name || ''}
            isOptionEqualToValue={(option, val) => {
              if (!option || !val) return false;
              const optionId = option.id ?? option.Id;
              const valId = val.id ?? val.Id;
              if (optionId == null || valId == null) return false;
              // eslint-disable-next-line eqeqeq
              return optionId == valId;
            }}
            noOptionsText={noOptionsContent}
            filterOptions={filterFn}
            ListboxComponent={
              normalizedData.length > 200 ? VirtualizedListbox : undefined
            }
            loading={isLoading}
            disabled={disabled || readOnly}
            readOnly={readOnly}
            size={compact ? 'small' : size}
            popupIcon={<ArrowDownIcon />}
            clearText="Temizle"
            openText="Aç"
            closeText="Kapat"
            loadingText="Yükleniyor..."
            slotProps={memoizedComponentsProps}
            renderOption={renderOption}
            renderInput={renderInput}
            sx={autocompleteSx}
          />
          {showSelectedChip && value && (
            <Chip
              label={value.name}
              size="small"
              color="primary"
              variant="outlined"
              onDelete={() => onChange(null)}
              sx={{
                mt: 1,
                fontWeight: 500,
                borderRadius: 2,
                '& .MuiChip-deleteIcon': {
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    color: 'error.main'
                  }
                }
              }}
            />
          )}
        </span>
      </Tooltip>
    );
  }
);

Combo.displayName = 'Combo';

Combo.propTypes = {
  tableName: PropTypes.string,
  filter: PropTypes.string,
  functionName: PropTypes.string,
  functionParameters: PropTypes.any,
  label: PropTypes.string,
  idField: PropTypes.string,
  textField: PropTypes.string,
  connectionName: PropTypes.string,
  value: PropTypes.shape({
    name: PropTypes.string,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }),
  values: PropTypes.array,
  defaultValue: PropTypes.any,
  setHataMetin: PropTypes.func,
  setHata: PropTypes.func,
  noEmpty: PropTypes.bool,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  required: PropTypes.bool,
  placeholder: PropTypes.string,
  size: PropTypes.string,
  variant: PropTypes.string,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  showSelectedChip: PropTypes.bool,
  compact: PropTypes.bool,
  staleTime: PropTypes.number,
  cacheTime: PropTypes.number,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  name: PropTypes.string,
  sx: PropTypes.object,
  loading: PropTypes.bool,
  InputProps: PropTypes.object,
  key: PropTypes.any,
  children: PropTypes.any,
  orderBy: PropTypes.oneOfType([
    PropTypes.oneOf(['name', 'id']),
    PropTypes.func
  ]),
  searchSplitChar: PropTypes.string,
  optionFilter: PropTypes.func,
  listProps: PropTypes.shape({
    key: PropTypes.any
  })
};

export default Combo;
